import { Paper, Skeleton, Card, Text, Group, Divider, Menu, ScrollArea, Stack, Box, RadioGroup, Radio } from '@mantine/core';
import { format as formatDate, formatDistance, formatDistanceToNow, formatDistanceToNowStrict, isValid as isDateValid } from 'date-fns';

import { AutoRefreshInfo, AutoRefreshToggle } from './AutoRefresh';
import { useDataProvider } from '../../../data_provider/StandardDataProvider';
import { useEffect, useState } from 'react';
import { DataLoadingError } from './Common';
import _ from "lodash"


const RealtimeDataWidget = ({}) => {

    const {data, requestId, isDataReady, isLoading, isError, errorMessage, lastRefreshTs, refreshData, autoRefreshEnabled, enableAutoRefresh, disableAutoRefresh} = useDataProvider({
        requestMethod: "GET",
        requestUrl: "/api/dashboard/lighthouse/data/realtime",
        resetDataOnRequest: false
    })

    const [gridDisplayMode, setGridDisplayMode] = useState("grid")
    const [timestampDisplayMode, setTimestampDisplayMode] = useState("both")

    console.log(data)
    useEffect(() => {
        //refreshData()
        enableAutoRefresh()
    }, [])

    return (
        <Card p={0} withBorder style={{flexGrow: 1, flexShrink: 1, height: "100%"}}>
            <Group px={10} pt={10} pb={5} position="apart" align="baseline">
                <Text weight={500}>Realtime Overview</Text>
                <Group spacing={4}>
                    <AutoRefreshInfo
                        lastRefreshTs={lastRefreshTs}
                        isLoading={isLoading}
                        isError={isError}
                        autoRefreshEnabled={autoRefreshEnabled}
                    />
                    <Menu>
                        <Paper p="sm">
                            <AutoRefreshToggle
                                autoRefreshEnabled={autoRefreshEnabled}
                                enableAutoRefresh={enableAutoRefresh}
                                disableAutoRefresh={disableAutoRefresh}
                            />
                        </Paper>
                        <Paper p="sm">
                            <GridDisplayRadio
                                value={gridDisplayMode}
                                setValue={setGridDisplayMode}
                            />
                        </Paper>
                        <Paper p="sm">
                            <TimestampDisplayRadio
                                value={timestampDisplayMode}
                                setValue={setTimestampDisplayMode}
                            />
                        </Paper>
                    </Menu>
                </Group>
            </Group>
            <Divider />
            <Stack p={0} style={{height: "100%"}}>
                {isError && <DataLoadingError message={errorMessage} m={15} />}
                {!data && !isError && isLoading && <DataLoadingSkeleton />}
                {(gridDisplayMode==="grid" && data) &&
                    <RealtimeGrid
                        data={data}
                        timestampDisplayMode={timestampDisplayMode}
                    />
                }
                {(gridDisplayMode==="panels" && data) &&
                    <ScrollArea>
                        <Group spacing="xs" align="normal" p={15} pb={60}>
                            {data.map(panelData => {
                                return (
                                    <SiteSummaryCard
                                        key={panelData.panelId}
                                        data={panelData}
                                        timestampDisplayMode={timestampDisplayMode}
                                    />
                                )
                            })}
                        </Group>
                    </ScrollArea>
                }
            </Stack>
        </Card>
    )
}

const RealtimeGrid = ({data, timestampDisplayMode}) => {

    const gridColumnLabelMap = Object.fromEntries(_.sortBy(Array.from(new Set(data.map(x => x.tags).flat().map(x => x.label)))).map((x, i) => [x, i+2]))

    return (
        <div style={{
            display: "grid",
            gridTemplateColumns: `repeat(${Object.keys(gridColumnLabelMap).length+1}, fit-content(100%))`,
            gap: 15,
            overflow: "auto",
            padding: "15px 15px 50px 15px"
        }}>
            {Object.entries(gridColumnLabelMap).map(x => {
                return (
                    <Text underline weight={500} style={{gridRow: 1, gridColumn: x[1], width: "max-content"}}>{x[0]}</Text>
                )
            })}
            {data.map((tagGroup, i) => {
                return tagGroup.tags.map(tag => {

                    const timestampDate = new Date(tag.timestamp)
                    const formattedValue = typeof(tag.value) === "number" ? tag.value.toLocaleString(undefined, {maximumFractionDigits: 2}) : null
                    
                    return (
                        <div style={{gridRow: i+2, gridColumn: gridColumnLabelMap[tag.label], width: "max-content"}}>
                            <div style={{display: "flex", flexDirection: "row", alignItems: "baseline", paddingRight: "0px"}}>
                                <Text weight={500}>{formattedValue}</Text>
                                {tag.units ? <Text weight={500} size="xs" color="dimmed" component="i" style={{marginLeft: "4px"}}>{tag.units}</Text> : null}
                            </div>
                            {["both", "relative"].includes(timestampDisplayMode) && <RelativeTimestampDisplay date={timestampDate} />}
                            {["both", "absolute"].includes(timestampDisplayMode) && <AbsoluteTimestampDisplay date={timestampDate} />}
                        </div>
                    )
                }).concat(
                    <Box style={{gridRow: i+2, gridColumn: 1, marginRight: "15px", maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", "-webkit-line-clamp": "5", "-webkit-box-orient": "vertical"}}>
                        <Text weight={500}>{tagGroup.projectName}</Text>
                        <Text color="dimmed" size="xs">{tagGroup.tagGroupName}</Text>
                    </Box>
                )
            })}
        </div>
    )
}

const DataLoadingSkeleton = () => {
    return (
        <Group m={15}>
            <Skeleton height="210px" width="200px" mr="15px" />
            <Skeleton height="210px" width="200px" mr="15px" />
            <Skeleton height="210px" width="200px" mr="15px" />
        </Group>
    )
}

const SiteSummaryCard = ({data, timestampDisplayMode}) => {

    return (
        <Card withBorder style={{minWidth: "275px", maxWidth: "400px"}}>
            <Group position="center">
                <Text weight={500}>{data.projectName}</Text>
            </Group>
            {data.tags.map(tag => {
                return (
                    <SummaryCardMetric
                        name={tag.label}
                        timestamp={tag.timestamp}
                        value={tag.value}
                        units={tag.units}
                        timestampDisplayMode={timestampDisplayMode}
                    />
                )
            })}
        </Card>
    )
}

const SummaryCardMetric = ({name, timestamp, value, units, timestampDisplayMode}) => {

    const timestampDate = new Date(timestamp)
    const formattedValue = typeof(value) === "number" ? value.toLocaleString(undefined, {maximumFractionDigits: 2}) : null

    return (
        <div>
            <Group spacing={0} position="apart" align="baseline">
                <Group spacing={0} align="baseline">
                    <Text weight={500}>{formattedValue}</Text>
                    <Text weight={500} size="xs" color="dimmed" component="i" style={{marginLeft: "4px"}}>{units}</Text>
                </Group>
                {["both", "relative"].includes(timestampDisplayMode) &&
                    <RelativeTimestampDisplay date={timestampDate} />
                }
            </Group>
            <Group spacing={0} align="baseline" position="apart">
                <Text size="xs" color="dimmed" transform="uppercase">{name}</Text>
                {["both", "absolute"].includes(timestampDisplayMode) &&
                    <AbsoluteTimestampDisplay date={timestampDate} />
                }
            </Group>
        </div>
    )
}

const AbsoluteTimestampDisplay = ({date}) => {
    
    const dateFormatted = isDateValid(date) ? formatDate(date, "h:mm:ss b, LLL d") : null

    return (
        <Text size="xs" color="dimmed">{dateFormatted}</Text>
    )
}

const RelativeTimestampDisplay = ({date}) => {

    // USed to force rerender
    const [ctr, setCtr] = useState(0)

    useEffect(() => {
        const timer = setInterval(update, 2000)
        return () => {
            clearInterval(timer)
        }
    }, [])

    const update = () => {
        setCtr((ctr_) => ctr_+1)
    }

    const timeAgo = isDateValid(date) ? formatDistanceToNowStrict(date, {includeSeconds: true, addSuffix: true}) : "?"

    return (
        <Text size="xs" color="dimmed">{timeAgo}</Text>
    )
}

const TimestampDisplayRadio = ({value, setValue}) => {
    return (
        <RadioGroup
            value={value}
            onChange={(v) => setValue(v)}
            label="Timestamp Display"
            size="xs"
        >
            <Radio value="both" label="Both" />
            <Radio value="absolute" label="Absolute" />
            <Radio value="relative" label="Relative" />
        </RadioGroup>
    )
}

const GridDisplayRadio = ({value, setValue}) => {
    return (
        <RadioGroup
            value={value}
            onChange={(v) => setValue(v)}
            label="Display Mode"
            size="xs"
        >
            <Radio value="grid" label="Grid" />
            <Radio value="panels" label="Panels" />
        </RadioGroup>
    )
}

export default RealtimeDataWidget